import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import moment from "moment";
import { ColumnDefaultRow, ColumnDefaultsResponse } from "../types";

// TAG_EDB_CUSTOM: site-wide defaultColDef additions. Spread this into each
// page's own defaultColDef (it's a partial ColDef, not a full grid config --
// every page still defines its own width/flex/filter etc. on top of this).
// No explicit ColDef<T> annotation here on purpose -- it would pin `field`
// to ColDef<any>'s widened string type, which then fails to spread into any
// page's more specific ColDef<SomeRow> defaultColDef (TS2322). `satisfies`
// checks this object's shape against ColDef without widening its inferred
// type, so it spreads cleanly into any page's defaultColDef.
export const EDB_CUSTOM_defaultColDef = {
  // Hides only the header "..." menu icon -- right-click on the header still
  // opens the same column menu (suppressHeaderContextMenu is left at its
  // default of false). Was crowding narrow column headers (e.g. doc_from).
  suppressHeaderMenuButton: true,
} satisfies Partial<ColDef>;

// TAG_EDB_CUSTOM: site-wide default header hover tooltip -- "{column name} -
// right-click for options", so the full column name is still discoverable
// even when the visible header text is truncated/obscured (e.g. by the sort
// arrow). NOT something that can live on EDB_CUSTOM_defaultColDef above --
// AG Grid's `headerTooltip` is a plain static string per column (confirmed:
// no header-tooltip equivalent of `tooltipValueGetter`, which only exists
// for body cells), so a single shared defaultColDef object has no way to
// see each column's own headerName to interpolate it. This instead maps
// over a colDefs array once, after it's built, filling in `headerTooltip`
// for each column from its own headerName/field.
//
// Call this when building colDefs (e.g.
// `useState<ColDef[]>(EDB_CUSTOM_withHeaderTooltips([...]))`). Overriding:
//   - per column: set that colDef's own `headerTooltip` explicitly -- this
//     function only fills in columns that don't already have one.
//   - per grid: don't call this function at all, or pass a different/empty
//     `suffix`.
const EDB_CUSTOM_HEADER_TOOLTIP_SUFFIX = " - right-click for options";

export function EDB_CUSTOM_withHeaderTooltips<TData = any>(
  colDefs: ColDef<TData>[],
  suffix: string = EDB_CUSTOM_HEADER_TOOLTIP_SUFFIX,
): ColDef<TData>[] {
  return colDefs.map((colDef) => {
    if (colDef.headerTooltip !== undefined) return colDef;
    const label = colDef.headerName ?? (typeof colDef.field === "string" ? colDef.field : undefined);
    if (!label) return colDef;
    return { ...colDef, headerTooltip: `${label}${suffix}` };
  });
}

export const caseInsensitiveComparator = (valueA : any, valueB: any) => {
    // Handle null or undefined values first
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return -1;
    if (valueB == null) return 1;
  
    // Check if both values are strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      const valA = valueA.toLowerCase();
      const valB = valueB.toLowerCase();
  
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    }
  
    // Fallback to default comparison for other types
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
};

export const timezoneIndependantDateComparator = (filterDate : Date, cellValue : any) => {
    if (!cellValue) return -1; // Treat empty/null as less than any filter date

    // Normalize to UTC for comparison
    const cellDate = new Date(cellValue);
    const normalizedCellDate = Date.UTC(
      cellDate.getFullYear(),
      cellDate.getMonth(),
      cellDate.getDate()
    );
  
    const normalizedFilterDate = Date.UTC(
      filterDate.getFullYear(),
      filterDate.getMonth(),
      filterDate.getDate()
    );
  
    if (normalizedCellDate < normalizedFilterDate) return -1;
    if (normalizedCellDate > normalizedFilterDate) return 1;
    return 0; // Dates are equal
};

export const dateValueFormatter = (params: ValueFormatterParams) => {
    if (!moment(params.value).isValid()) return params.value;
    return moment(params.value).format('DD/MM/YYYY')
}

export const dateValueFormatter_iso = (params: ValueFormatterParams) => {
    if (!moment(params.value).isValid()) return params.value;
    return moment(params.value).format('YYYY-MM-DD')
}

export const dateTimeValueFormatter = (params: ValueFormatterParams) => {
    if (!moment(params.value).isValid()) return params.value;
    return moment(params.value).format('DD/MM/YYYY HH:mm:ss')
}

export const dateTimeValueFormatter_iso = (params: ValueFormatterParams) => {
    if (!moment(params.value).isValid()) return params.value;
    return moment(params.value).format('YYYY-MM-DD HH:mm')
}

export const yesNoValueFormatter = (params: ValueFormatterParams) => {
  if (params.value === true || params.value === "true") return "Yes";
  else return "No";
}

export const poundValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return params.value
  else return `£${params.value}`;
}

export const penceValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return params.value
  else return `${params.value}p`;
}

export const kwhValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return params.value
  else return `${params.value} kWh`;
}

export const percentValueFormatter = (params: ValueFormatterParams) => {
  if (!params.value) return params.value
  else return `${params.value}%`;
}

// gui.column_default's column_type -> which formatter function applies. Not every type has one
// (text/number render the raw value) -- see office-portal/design/gui_column_default_ag_grid_reference.md.
const FORMATTER_BY_COLUMN_TYPE: Record<string, (params: ValueFormatterParams) => any> = {
  bool: yesNoValueFormatter,
  date: dateValueFormatter,
  date_iso: dateValueFormatter_iso,
  datetime: dateTimeValueFormatter,
  datetime_iso: dateTimeValueFormatter_iso,
  currency: poundValueFormatter,
  pence: penceValueFormatter,
  kwh: kwhValueFormatter,
  percent: percentValueFormatter,
};

// gui.column_default's comparator column -> named SORT comparator (see the DDL/reference doc
// comment on why timezoneIndependantDateComparator is NOT here -- it's a date-filter comparator,
// a different AG Grid hook with a different call signature).
const COMPARATOR_BY_NAME: Record<string, (valueA: any, valueB: any) => number> = {
  caseInsensitive: caseInsensitiveComparator,
};

// DB-driven ag-grid column defaults (gui.column_default) -- flat, one row per column_name, no
// tiering. Returns a plain object to spread FIRST in a page's own defaultColDef/ColDef literal,
// per the confirmed precedence: DB default -> code ColDef (spread after this, still wins) ->
// user's saved localStorage grid state (applied separately via PersistentGrid's initialState,
// unaffected by this function). A column with no matching row just gets EDB_CUSTOM_defaultColDef
// -- same as before this feature existed.
// No explicit ColDef/Partial<ColDef> return-type annotation (same reasoning as
// EDB_CUSTOM_defaultColDef above) -- the returned object never has a `field` key, so leaving the
// type inferred (via `satisfies`, which doesn't widen) lets it spread into any page's more
// specific ColDef<SomeRow> without TS pinning `field` to a generic string.
export function buildDefaultColDef(
  defaults: ColumnDefaultsResponse | null | undefined,
  columnName?: string,
) {
  const row: ColumnDefaultRow | undefined = columnName ? defaults?.[columnName] : undefined;
  if (!row) return { ...EDB_CUSTOM_defaultColDef } satisfies Partial<ColDef>;

  // 'true'/'false' are DB string sentinels (a column can't natively hold ColDef's
  // boolean | string union) -- translate back to real booleans.
  const filter = row.filter === "true" ? true : row.filter === "false" ? false : row.filter;
  const cellDataType =
    row.cellDataType === "true" ? true : row.cellDataType === "false" ? false : (row.cellDataType ?? undefined);
  const formatter = FORMATTER_BY_COLUMN_TYPE[row.columnType];
  const comparator = row.comparator ? COMPARATOR_BY_NAME[row.comparator] : undefined;

  return {
    ...EDB_CUSTOM_defaultColDef,
    ...(row.width != null ? { width: row.width } : {}),
    minWidth: row.minWidth,
    ...(row.maxWidth != null ? { maxWidth: row.maxWidth } : {}),
    ...(row.flex != null ? { flex: row.flex } : {}),
    resizable: row.resizable,
    hide: row.hide,
    ...(row.pinned != null ? { pinned: row.pinned } : {}),
    lockPosition: row.lockPosition,
    lockPinned: row.lockPinned,
    lockVisible: row.lockVisible,
    suppressMovable: row.suppressMovable,
    sortable: row.sortable,
    ...(row.sort != null ? { sort: row.sort } : {}),
    ...(row.sortIndex != null ? { sortIndex: row.sortIndex } : {}),
    unSortIcon: row.unSortIcon,
    ...(comparator ? { comparator } : {}),
    filter,
    floatingFilter: row.floatingFilter,
    ...(row.filterParams ? { filterParams: row.filterParams } : {}),
    editable: row.editable,
    ...(row.cellEditor != null ? { cellEditor: row.cellEditor } : {}),
    ...(row.cellEditorParams ? { cellEditorParams: row.cellEditorParams } : {}),
    enableRowGroup: row.enableRowGroup,
    rowGroup: row.rowGroup,
    ...(row.rowGroupIndex != null ? { rowGroupIndex: row.rowGroupIndex } : {}),
    enablePivot: row.enablePivot,
    pivot: row.pivot,
    ...(row.aggFunc != null ? { aggFunc: row.aggFunc } : {}),
    checkboxSelection: row.checkboxSelection,
    headerCheckboxSelection: row.headerCheckboxSelection,
    ...(cellDataType !== undefined ? { cellDataType } : {}),
    wrapText: row.wrapText,
    autoHeight: row.autoHeight,
    ...(row.headerTooltip != null ? { headerTooltip: row.headerTooltip } : {}),
    ...(formatter ? { valueFormatter: formatter } : {}),
  } satisfies Partial<ColDef>;
}